import {
  experimental_ComboboxMultipleField as ComboboxMultipleField,
  experimental_ComboboxMultiplePopover as ComboboxMultiplePopover,
  csx,
  Skeleton,
  experimental_useComboboxMultipleState as useComboboxMultipleState,
} from '@vtex/admin-ui'
import {
  FormState,
  getErrorText,
  hasError,
  useWatch,
} from '@vtex/admin-ui-form'
import React, { useEffect, useMemo } from 'react'

import { useIntl } from 'react-intl'
import type { Item } from 'typings'
import { useFormStateContext } from '../../providers'
import { FieldDetails } from '../FieldDetails'
import { searchItems } from '../utils'
import { ItemRenderer } from './ItemRenderer'
import { styles } from './styles'
import { validationSuite } from './validation'

interface Props {
  name: string
  options: Item[]
  loading?: boolean
  loadingOptions?: boolean
  label: string
  helpText?: string
  onSearch?: (value: string) => void
  renderOption?: (item: Item) => React.ReactNode
  renderTag?: (item: Item) => React.ReactNode
  disabled?: boolean
  maxEntries?: number
  state?: FormState
}

function Loading() {
  return <Skeleton className={csx({ height: 60, widht: 144 })} />
}

export function MultiSelect(props: Props) {
  const {
    name,
    options,
    loading,
    loadingOptions,
    label,
    helpText,
    onSearch,
    renderOption,
    renderTag,
    disabled,
    maxEntries,
    state: formState,
  } = props

  const { form: formContext, readOnly } = useFormStateContext()

  const intl = useIntl()

  const suite = useMemo(
    () => validationSuite(name, intl, maxEntries),
    [name, intl, maxEntries]
  )

  const isDisabled = disabled || readOnly

  const form = formState ?? formContext

  const items: Item[] = useWatch({ form, name })

  const comboboxMultipleState = useComboboxMultipleState({
    defaultSelected: items,
    renderOption: (item) => {
      const validation = suite(items.length)

      if (!validation.isValid()) {
        return (validation.errors[0].message as string) ?? ''
      }

      return renderOption?.(item) ?? <ItemRenderer item={item} />
    },
    getOptionValue: (item) => String(item?.id),
    renderTag:
      renderTag ?? ((item) => <ItemRenderer item={item} maxWidth={200} />),
  })

  useEffect(() => {
    comboboxMultipleState.selectedItems !== items &&
      comboboxMultipleState.setSelectedItems(items)
  }, [items])

  useEffect(() => {
    comboboxMultipleState.setLoading(!!loadingOptions)
  }, [loadingOptions])

  useEffect(() => {
    const validation = suite(comboboxMultipleState.selectedItems.length)

    if (validation.isValid()) {
      comboboxMultipleState.setMatches(options ?? [])
    } else {
      comboboxMultipleState.setMatches([options[0]])
    }
  }, [options, comboboxMultipleState.selectedItems])

  useEffect(() => {
    comboboxMultipleState.selectedItems !== items &&
      form.setValue(name, comboboxMultipleState.selectedItems)
  }, [comboboxMultipleState.selectedItems])

  useEffect(() => {
    const validation = suite(comboboxMultipleState.selectedItems.length)

    if (validation.isValid()) {
      onSearch
        ? onSearch(comboboxMultipleState.deferredValue)
        : comboboxMultipleState.setMatches(
            searchItems(options, comboboxMultipleState.deferredValue)
          )
    }
  }, [comboboxMultipleState.deferredValue])

  if (loading) return <Loading />

  const error = hasError(form, name)

  return (
    <div>
      <ComboboxMultipleField
        id={name}
        state={comboboxMultipleState}
        label={label}
        className={`${styles.default} ${isDisabled ? styles.disabled : ''} ${
          error ? styles.error : ''
        }`}
        data-testid={name}
        onClick={(event) => event.stopPropagation()}
      />
      <ComboboxMultiplePopover
        state={comboboxMultipleState}
        className={styles.popover}
        data-disabled={!suite(items.length).isValid()}
        onClick={(event) => event.stopPropagation()}
        // @ts-expect-error admin-ui does not export portal prop,
        // but it uses ariakit under the hood which supports it
        // ref: https://ariakit.org/reference/combobox-popover#portal
        portal
      />
      <FieldDetails>
        <FieldDetails.Help text={helpText} />
        <FieldDetails.Error text={getErrorText(form, name)} />
        <FieldDetails.Counter
          count={comboboxMultipleState.selectedItems.length}
          max={maxEntries}
        />
      </FieldDetails>
    </div>
  )
}
